import React from 'react';
import moment from 'moment';
import { Alert, Button, Label } from 'react-bootstrap/lib';
import {
  LendingStateCode,
  RicStateSchemaContent,
  RicStateSchemaRevision,
} from 'hooks/services';
import { useRicStateSchemaRevisions } from 'hooks/api/ricStateSchemas';

/** Absent for every state until an engineer saves one, since the seed has no actor. */
const editorOf = (revision: RicStateSchemaRevision) =>
  revision.editedBy ?? 'Script (no user)';

export const describeRevision = (revision: RicStateSchemaRevision) =>
  `${moment(revision.createdAt).format('llll')} by ${editorOf(revision)}`;

function RicSchemaRevisionHistory({
  stateCode,
  currentContent,
  onLoadRevision,
}: {
  stateCode: LendingStateCode;
  currentContent: RicStateSchemaContent;
  onLoadRevision: (revision: RicStateSchemaRevision) => void;
}) {
  const { revisions, isLoadingRevisions, revisionsError } =
    useRicStateSchemaRevisions(stateCode);

  // Matched on content rather than taken to be the newest row: the newest revision
  // is what this environment serves for every write path that exists, but a row
  // written around the model's hooks would make that a lie on the one screen whose
  // job is provenance. Nothing is labelled if nothing matches.
  const currentJson = JSON.stringify(currentContent);
  const isCurrent = (revision: RicStateSchemaRevision) =>
    JSON.stringify(revision.content) === currentJson;

  const renderBody = () => {
    if (isLoadingRevisions) {
      return <Alert>Loading revisions...</Alert>;
    }

    if (revisionsError) {
      return (
        <Alert bsStyle="danger">
          Could not load the revision history: {revisionsError.message}
        </Alert>
      );
    }

    if (!revisions.length) {
      return <Alert>No edits yet for this state.</Alert>;
    }

    return (
      <ul className="ric-schema-revision-list">
        {revisions.map((revision) => (
          <li key={revision.id}>
            {/* One cell, so the buttons still line up under each other. */}
            <span className="ric-schema-revision-summary">
              {describeRevision(revision)}
              {isCurrent(revision) && <Label bsStyle="success">Current</Label>}
            </span>
            <Button bsSize="small" onClick={() => onLoadRevision(revision)}>
              Load into editor
            </Button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="ric-schema-revision-history">
      <h4>Recent saves</h4>
      {renderBody()}
    </div>
  );
}

export default RicSchemaRevisionHistory;
